<template>
    <div>
        <div class="d-flex flex-row align-items-center flex-wrap mb-2 position-sticky" style="left: 0; gap: 0.5rem">
            <!-- Student - Group View -->
            <template v-if="!editMode" class="d-flex">
                <YesNoToggle
                    v-model="studentView"
                    v-show="students.length && groups.length"
                    :yesLabel="studentsAreTeachers ? $t('teachers') : $t('students')" 
                    :noLabel="programme ? $t('groups') : $t('events')" 
                    :label="$t('view-mode')"
                    :srOnly="true"
                    class="mb-0 mr-1"
                />
                
                <template v-if="timetable">
                    <button type="button" class="btn btn-light" @click="prevWeek">
                        <b-icon icon="chevron-left" />
                    </button>
                    <SelectInput 
                        class="m-0 p-0" 
                        :label="$t('week')" 
                        :srOnly="true"
                        :options="fullWeekOptions"
                        :indexIsValue="true"
                        :value="null"
                        @input="toWeek"
                        :nullable="true"
                        :nullLabel="$t('select-a-week')"
                    />
                    <button type="button" class="btn btn-light" @click="nextWeek">
                        <b-icon icon="chevron-right" />
                    </button>
                </template>
                
                <button
                    v-if="showJumpToTodayButton"
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="jumpToCurrentWeek"
                    >
                    <b-icon icon="calendar" class="mr-1" />{{ $t('today') }}
                </button>



                
            </template>

            <template v-else-if="eventEditor">
                <!-- Add students to events or remove/swap ??? -->
                <YesNoToggle 
                    :value="addMode"
                    @input="val => toggleAddMode(val)"
                    :yesLabel="$t('add')"
                    :noLabel="$t('edit')"
                    class="mb-0 mr-1"
                    :label="$t('edit-mode')"
                    :srOnly="true"
                />

                <!-- If editing  -->
                <template v-if="!addMode">
                    <button 
                        :disabled="!eventEditor.countSelected()"
                        type="button" 
                        class="btn btn-outline-danger mr-1"
                        @click="removeSelected"
                    >
                        {{ $t('remove') }}
                    </button>
                </template>
                
                <button
                    :disabled="!eventEditor.canUndo()"
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="undo"
                >
                    <b-icon icon="arrow-counterclockwise" class="mr-1"/>{{ $t('undo') }}
                </button>
            </template>

            <!-- Toggle edit mode -->
            <button v-if="studentView && !editMode && allowEdit && $UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN')" type="button" class="btn ml-auto btn-outline-secondary" @click="toggleEditMode(true)">
                <b-icon icon="pencil" class="mr-1" />{{ $t('edit') }}
            </button>
            
            <template v-if="editMode && allowEdit">
                <!-- Save / exit -->
                <button type="button" class="btn btn-primary ml-auto" @click="showSaveDialog">
                    <b-icon icon="check-all" class="mr-1"/>{{ $t('save') }}
                </button>
                <button type="button" class="btn btn-outline-dark ml-2" @click="toggleEditMode(false)">
                    <b-icon icon="arrow-return-right" class="mr-1"/>{{ $t('back') }}
                </button>
        </template>
        </div>

        <!-- Events available for the dates selected -->
        <div v-if="addMode && allowEdit" class="d-flex flex-row align-items-start" style="gap: 0.5rem; max-width: 100%; overflow: hidden; overflow-x: auto; min-height: 3.5rem;">
            <div v-if="!eventEditor.selectedDayCount()" class="alert alert-info w-100 mb-0">
                {{ $t('events-available-msg') }}
            </div>
            <TimetableEvent 
                v-for="event in addEvents"
                :key="event.id"
                :event="event"
                @click="addToSelected(event)"
                class="text-nowrap"
                style="padding: 0.25rem; font-size: 0.6rem; font-weight: bold; min-width: fit-content"
            />
        </div>
       

        <!-- TIMETABLE -->
        <div ref="timetable" class="sticky-container" :style="{ boxShadow: editMode ? '0 0 1000px 100px rgba(0,0,0,0.2)' : ''}">
            <table class="table table-bordered bg-white table-timetable" :class="zoomClass">
            <thead>
                <!-- Weeks -->
                <tr class="border-top" v-if="!dense">
                    <!-- Sticky Left Column Header -->
                    <th
                        scope="row"
                        class="border-bottom-0 text-nowrap bg-white"
                        style="position: sticky; top: 0; left: 0; z-index: 5;"
                        >
                        {{ $t('year-group') }}
                    </th>

                    <!-- Week Headers -->
                    <th 
                        v-for="(weekDate, index) in weekStartDates" 
                        :key="index" 
                        :colspan="index === 0 ? firstWeekLen : index === weekStartDates.length - 1 ? lastWeekLen : hideWeekends ? 5 : 7"
                        ref="weeks"
                        class="text-nowrap"
                        >
                        W/C {{ formatDate(weekDate) }}
                        
                    </th>
                </tr>



                <!-- Dates -->
                <tr>
                    <th scope="row" ref="topLeftCell" />
                    <template v-for="(months, year) in daysByYearAndMonth">
                    <template v-for="(days, month) in months">
                    <th 
                        v-for="(day, date) in days" 
                        :key="`${year}-${month}-${date}`"
                        :id="`${year}-${month}-${date}`"
                        ref="days"
                        class="px-1 text-nowrap"
                        :class="{ 'bg-light': [0, 6].includes(day.day) }"
                    >
                        {{ `${$t('dayname-short-' + (day.day === 0 ? 7 : day.day))}` }}<br>
                        {{ `${date} / ${Number(month) + 1}` }}
                    </th>
                    </template>
                    </template>
                </tr>
            </thead>
            <tbody>

                <!-- Events by Year + Group -->
                <template v-if="!studentView">
                <template v-for="y in Number(programme ? programme.CourseLengthYears : 1)">
                <!-- Year -->
                <tr 
                    v-if="(!dense || studentsByYear[y]) && programme" v-show="!years.length || years.findIndex(yr => yr == y) > -1" 
                    :class="{'text-white bg-dark': !hideGroups,
                        'text-black bg-white': hideGroups
                    }" 
                    :key="y">
                    <th 
                        scope="row" 
                        @click="yearColumnEventCb(y)" 
                        class="text-nowrap"
                        :class="{'bg-white px-1 text-info' : hideGroups}"
                    >
                        {{ programme.Name + ' ' + y }}
                    </th>
                    <td :colspan="numDays" v-if="!hideGroups" />
                    <!--Collapsed groups // Only years-->
                    <template v-if="hideGroups">
                    <template v-for="(months, year) in daysByYearAndMonth">
                    <template v-for="(days, month) in months">

                    <!-- Days -->
                    <td 
                        v-for="(day, date) in days" 
                        :key="`${year}-${month}-${date}`"
                        :class="{ 'bg-light': [0, 6].includes(day.day) }"
                    >
                        <!-- Events -->
                        <template v-if="hasEventsForYearAndProgramme(day,y,programme.Id)">
                        <TimetableEvent 
                            v-for="(event, i) in filteredEventsByYear(day, y, programme.Id)"
                            :key="`${y}-${event.id}-${i}`"
                            :event="event"
                            @click="$emit('selectEvent', event)"
                            @contextmenu.native.prevent="$emit('selectEvent',{event:event, modal:true})"
                            class="timetable-event text-nowrap"
                            :showTeacherDetails="studentsAreTeachers"
                            :showSharedEvents="$config.getValue('showsharedevents')"
                            :slim="slim"
                            :style="{
                                fontSize: zoom === 3 ? '0.6rem' : zoom === 2 ? '0.5rem' : '0.4rem'
                            }"
                            :displayGroups="hideGroups"
                        />
                        </template> 
                    </td>
                    </template>
                    </template>
                    </template>
                </tr>
                
                <!-- Group -->
                <template v-if="!hideGroups"> 
                <tr v-for="g in groupsByYear[y]" :key="`${g.id}-${y}`" v-show="(!years.length || years.findIndex(yr => yr == y) > -1) && (!groups.length || groups.findIndex(gr => gr.id === g.id) != -1)">
                    <th 
                        class="font-weight-normal bg-white px-1" 
                        scope="row" 
                        @click="groupColumnEventCb(g)"
                    >
                        {{ g.title }}
                    </th>
                    <template v-for="(months, year) in daysByYearAndMonth">
                    <template v-for="(days, month) in months">

                    <!-- Days -->
                    <td 
                        v-for="(day, date) in days" 
                        :key="`${year}-${month}-${date}`"
                        :class="{ 'bg-light': [0, 6].includes(day.day) }"
                    >
                        <!-- Events -->
                        <template v-if="day.eventsByGroup[g.id]">
                            
                        <TimetableEvent 
                            v-for="(event, i) in  day.eventsByGroup[g.id].filter(e => matchesRoomFilter(e))"
                            :key="`${event.id}-${i}`"
                            :event="event"
                            @click="$emit('selectEvent', event)"
                            @contextmenu.native.prevent="$emit('selectEvent',{event:event, modal:true})"
                            class="timetable-event text-nowrap"
                            :showTeacherDetails="studentsAreTeachers"
                            :showSharedEvents="$config.getValue('showsharedevents')"
                            :slim="slim"
                            :style="{
                                fontSize: zoom === 3 ? '0.6rem' : zoom === 2 ? '0.5rem' : '0.4rem'
                            }"
                        />
                        </template>
                    </td>
                    </template>
                    </template>
                </tr>
                </template>
                </template>
                </template>

                <!-- Events by Student -->
                <template v-else>
                    <tr 
                        v-for="student in filteredStudents"
                        :key="`student-${student.id}`"
                        ref="students"
                    >
                        <th class="font-weight-normal bg-white" :class="{'text-nowrap': slim }" scope="row" @click="$emit('selectUser', student)">{{ student.displayname }}</th>
                        <template v-for="(months, year) in daysByYearAndMonth">
                        <template v-for="(days, month) in months">

                        <!-- Days -->
                        <td 
                            v-for="(day, date) in days" 
                            :key="`${year}-${month}-${date}`"
                            :class="{ 'bg-light': [0, 6].includes(day.day) }"
                            :style="{
                                position: 'relative',
                                backgroundColor: isAbsent(year, month, date, student.id) ? '#FF9999 !important' : '',
                            }"
                            @click="addMode ? toggleDay(year, month, date, student, day.eventsByStudent[student.id]) : undefined"
                        >
                            <!-- Events -->
                            <div v-if="day.eventsByStudent[student.id]" :class="{'d-flex flex-row': slim}" :style="{ gap: slim ? '2px' : '0' }">
                            <TimetableEvent
                                v-for="(event, i) in day.eventsByStudent[student.id].filter(e => matchesRoomFilter(e))"
                                :key="`${event.id}-${i}`"
                                :event="event"
                                @click="$event => addMode
                                    ? undefined
                                    : editMode 
                                    ? toggleEvent(year, month, date, student.id, event.id) 
                                    : $emit('selectEvent', event)
                                "
                                class="timetable-event text-nowrap"
                                :slim="slim"
                                :style="{
                                    fontSize: zoom === 3 ? '0.6rem' : zoom === 2 ? '0.5rem' : '0.4rem',
                                    opacity: editMode && eventEditor.eventIsSelected(year, month, date, student.id, event.id) ? '0.5' : '1',
                                }"
                                :markAbsent="isAbsent(year, month, date, student.id, event)"
                            />
                            </div>

                            <!-- Selected Style -->
                            <div 
                                v-if="eventEditor && eventEditor.dayIsSelected(year, month, date, student.id)"
                                :style="{backgroundColor:'#FFCA00', opacity:0.5, position:'absolute', top:0, left:0, width:'100%', height:'100%'}"
                            />
                        </td>
                        </template>
                        </template>
                    </tr>
                </template>
            </tbody>
            </table>
        </div>
    </div>    
</template>

<script>
import SessionManager from 'src/modules/session/sessionManager'
import EventContainer from 'src/modules/timetable/eventContainer'
import EventEditor from 'src/modules/timetable/eventEditor'
import { dateFromISODate, dateToISODate, dateFromTimestamp } from 'src/modules/timetable/util'

import YesNoToggle from 'src/components/Inputs/YesNoToggle.vue'
import SelectInput from 'src/components/Inputs/SelectInput.vue'
import TimetableEvent from './TimetableEvent.vue'


export default {
    components: {
        YesNoToggle,
        TimetableEvent,
        SelectInput,
    },
    props: {
        from: String,   // ISO format date string: 'YYYY-MM-DD'
        until: String,  // ISO format date string: 'YYYY-MM-DD'
        programme: Object,
        events: {
            type: Array,
            default: () => [],
        },
        bgEvents: {
            type: Array,
            default: () => [],
        },
        // If not empty, will display specific years
        years: {
            type: Array,
            default: () => [],
        },
        groups: {
            type: Array,
            // Gives us a single group to display all events in when we want to show all events in a single row
            default: function() {
                return [{ id: -1, group_id: -1, year: 1, title: this.$t('events') }]
            },
        },
        rooms: {
            type: Array,
            default: () => [],
        },
        firm: {
            type: [String, Number],
            default: 0,
        },
        students: {
            type: Array,
            default: () => [],
        },
        absences: {
            type: Array,
            default: () => [],
        },
        zoom: {
            type: Number,
            default: 3,
        },
        // If true, hides information on events to slim row height
        slim: {
            type: Boolean,
            default: false,
        },
        // If true, hides columns and rows which contain no events
        dense: {
            type: Boolean,
            default: false,
        },
        // Hides weekend date columns
        hideWeekends: {
            type: Boolean,
            default: false,
        },
         // Hides weekend date columns
        hideGroups: {
            type: Boolean,
            default: false,
        },
        // If true, does not hide student view toggle on full calendar view: Only use with small timetables!
        showViewToggleAlways: {
            type: Boolean,
            default: false,
        },
        // If true, initially loads the timetable in student view
        studentViewOnLoad: {
            type: Boolean,
            default: false,
        },
        // Disables the session manager.
        disableSession: {
            type: Boolean,
            default: false,
        },
        // Use if the event 'students' passed are actually the event's assigned staff.
        // This is just to re-use the student display logic for teacher timetables.
        studentsAreTeachers: {
            type: Boolean,
            default: false,
        },
        // Must have same method signature as below
        yearColumnEventCb: {
            type: Function,
            default: function (year) {
                this.studentView = true
                this.$emit('showYear', year)
            },
        },
        // Must have same method signature as below
        groupColumnEventCb: {
            type: Function,
            default: function (group) {
                this.studentView = true
                this.$emit('showGroup', group)
            },
        },
        allowEdit: {
            type: Boolean,
            default: false,
        },
        timetable: {
            type: Object,
            default: null,
        },

        /** Scroll-pagination [DOES NOT SUPPORT BACKGROUND EVENTS] */

        // If true, emits 'back' and 'forward' events when scrolled to the start or end of the timetable.
        scrollPaginate: {
            type: Boolean,
            default: false,
        },
        minDate: String,
        maxDate: String,
    },
    created() {
        this.eventContainer = new EventContainer()
    },
    mounted() {
        this.eventContainer = new EventContainer(this.events, this.bgEvents)
        this.updateDays()

        if (this.groups.length === 1 && this.groups[0].id === -1) {
            this.groups.unshift(...this.getEventGroups())
        }

        this.computeFullWeekOptions()

        if (!this.disableSession) {
            const emit = this.$emit.bind(this)
            this.$emit = (key, value) => {
                emit(key, value)
                this.$nextTick(this.saveState)
            }
            this.readState()
        }

        this.$nextTick(() => {
            if (this.scrollPaginate) {
                this.$refs.timetable.addEventListener('scroll', this.handleScroll)
                this.scrollData.ranges.push({from: this.from, until: this.until})
            }

            this.ready = true
            this.scrollToCurrentWeek()
            this.$refs.timetable.scrollTop = 0
        })
    },

    updated() {
        if (this.scrollPaginate && !this.canScroll()) {
            if (this.until < this.maxDate) this.$emit('forward', this.scrollData.until)
            else if (this.from > this.minDate) this.$emit('back', this.scrollData.from)
        }
    },
    data() {
        return {
            eventContainer: null,
            innerEvents: this.events,
            groupsByYear: {},
            studentsByYear: {},
            daysByYearAndMonth: {},
            absencesByStudent: {},
            weekOptions: {},
            onWeek: 1,
            weekStartDates: [],
            ready: false,
            fullWeekStartDates: [],
            currentWeekInRenderedDays: false,
            fullWeekOptions: {},
            fromWeek: 0,
            numWeeks: 0,
            numDays: 0,
            startDate: null,
            endDate: null,
            studentView: this.studentViewOnLoad,
            sessionManager: new SessionManager(this.$route),
            isFiltering: false,

            /** Scroll-pagination */
            scrollData: {
                from: this.from,
                until: this.until,
                loadingEarlier: false,
                loadingLater: false,
                ranges: [],   // Stores YYYY-MM-DD date-string pairs {from, until} representing the chunks loaded, in the order fetched
                progScrollX: false,
                progScrollY: false,
                prevX: 0,
                prevY: 0,

                loading() {
                    return this.loadingEarlier || this.loadingLater
                }
            },

            /** Student-view sliding window */
            studentWindow: {
                from: 0,
                to: 60,
                total: 0,
                interval: 30,
                down() {
                    if (this.to >= this.total) return false
                    this.from += this.interval; this.to += this.interval; return true;
                },
                up() {
                    if (this.from <= 0) return false
                    this.from -= this.interval; this.to -= this.interval; return true;
                },
                setTotal(total) {
                    if (total < this.total) {
                        this.from = 0
                        this.to = 60
                    }
                    this.total = total
                }
            },

            editMode: false,
            addMode: false,
            eventEditor: null,
            addEvents: [],
        }
    },
    methods: {
        jumpToCurrentWeek() {
            const today = new Date();
            const monday = new Date(today);
            const day = monday.getDay();
            const diff = day === 0 ? -6 : 1 - day; 
            monday.setDate(monday.getDate() + diff);
            monday.setHours(0, 0, 0, 0);

            const startDate = dateToISODate(monday);
            const weekIndex = this.fullWeekStartDates.findIndex(date => dateToISODate(date) === startDate);

            if (weekIndex === -1) {
                console.warn('Current week is not in timetable range.');
                return;
            }
            this.toWeek(weekIndex + 1);  
        },

        computeFullWeekOptions() {
            if (!this.minDate || !this.maxDate) return;

            const start = dateFromISODate(this.minDate)
            const end = dateFromISODate(this.maxDate)

            let date = new Date(start)
            while (date.getDay() !== 1) date.setDate(date.getDate() - 1)

            let weekNo = 0
            let weekOptions = {}
            let weekStartDates = []

            const suffix = (d) => {
                if (d > 3 && d < 21) return 'th';
                switch (d % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            }

            while (date <= end) {
                weekNo++
                const weekStart = new Date(date)
                weekStartDates.push(weekStart)

                const dayNum = weekStart.getDate()
                const month = weekStart.toLocaleString('default', { month: 'short' })
                const year = weekStart.getFullYear()

                weekOptions[`${weekNo}`] = `W/C ${dayNum}${suffix(dayNum)} ${month} ${year}`
                date.setDate(date.getDate() + 7)
            }

            this.fullWeekOptions = weekOptions
            this.fullWeekStartDates = weekStartDates
        },
        formatDate(date) {
            if (!date) return '';

            const day = date.getDate()
            const month = date.toLocaleString('default', { month: 'short' })
            const year = date.getFullYear()

            const suffix = (d) => {
                if (d > 3 && d < 21) return 'th';
                switch (d % 10) {
                    case 1: return 'st';
                    case 2: return 'nd';
                    case 3: return 'rd';
                    default: return 'th';
                }
            }

            return `${day}${suffix(day)} ${month} ${year}`
        },

        /**
         * Saves user progress in the timetable for reloading between event views
         */
        saveState() {
            const state = {
                studentView: this.studentView,
                studentWindow: this.studentWindow,
                editMode: this.allowEdit ? this.editMode : undefined,
            }

            this.sessionManager.set('timetable-progress', state)
        },
        /**
         * Reads user progress in the timetable view from sessionManager and restores.
         * Also checks for query parameters that specify state.
         */
        readState() {
            const state = this.sessionManager.get('timetable-progress')
            if (state) {
                this.studentView = state.studentView,
                this.studentWindow = { ...this.studentWindow, ...state.studentWindow }
                if (this.allowEdit) this.toggleEditMode(!!state.editMode)
            }

            if (this.$route.query.year || this.$route.query.group || this.$route.query.firm) this.studentView = true
        },
        /**
         * Generates the structure to render the timetable from as a year -> month -> day -> details dictionary.
         */
         
        // updateDays() {
        //     const eventContainer = !this.editMode ? this.eventContainer : this.eventEditor;
        //     const from = this.scrollPaginate ? this.scrollData.from : this.from;
        //     const until = this.scrollPaginate ? this.scrollData.until : this.until;

        //     if (!from || !until) return;

        //     this.daysByYearAndMonth = {};
        //     this.weekStartDates = []; // Clear existing

        //     let day = 0;
        //     let weekNo = 0;
        //     let weekOptions = {};

        //     const start = dateFromISODate(from);
        //     const end = dateFromISODate(until);

        //     let date = new Date(start);
        //     while (date.getDay() !== 1) {
        //         date.setDate(date.getDate() - 1);
        //     }

        //     while (date <= end) {
        //         weekNo++;
        //         const weekStart = new Date(date);
        //         this.weekStartDates.push(weekStart);

        //         const dayNum = weekStart.getDate();
        //         const month = weekStart.toLocaleString('default', { month: 'short' });
        //         const year = weekStart.getFullYear();

        //         const suffix = (d) => {
        //             if (d > 3 && d < 21) return 'th';
        //             switch (d % 10) {
        //                 case 1: return 'st';
        //                 case 2: return 'nd';
        //                 case 3: return 'rd';
        //                 default: return 'th';
        //             }
        //         };

        //         weekOptions[`${weekNo}`] = `W/C ${dayNum}${suffix(dayNum)} ${month} ${year}`;

        //         date.setDate(date.getDate() + 7);
        //     }

        //     // Reset date to real start
        //     date = new Date(start);
        //     let week = date.getDay() === 1 ? 1 : 0;

        //     for (; date <= end; date.setDate(date.getDate() + 1)) {
        //         day++;

        //         if (date.getDay() === 1) week++;

        //         if (this.hideWeekends && (date.getDay() === 6 || date.getDay() === 0)) continue;

        //         if (!this.daysByYearAndMonth[date.getFullYear()]) this.daysByYearAndMonth[date.getFullYear()] = {};
        //         if (!this.daysByYearAndMonth[date.getFullYear()][date.getMonth()]) this.daysByYearAndMonth[date.getFullYear()][date.getMonth()] = {};

        //         if (this.dense && eventContainer.isEmpty(date.getFullYear(), date.getMonth(), date.getDate())) continue;

        //         this.daysByYearAndMonth[date.getFullYear()][date.getMonth()][date.getDate()] = {
        //             day: date.getDay(),
        //             week,
        //             eventsByGroup: eventContainer.getGroupEvents(date.getFullYear(), date.getMonth(), date.getDate()),
        //             eventsByStudent: eventContainer.getStudentEvents(date.getFullYear(), date.getMonth(), date.getDate())
        //         };

        //         const eventsByYear = eventContainer.getYearEvents(date.getFullYear(), date.getMonth(), date.getDate())

        //         let years = {}
        //         for (let year in eventsByYear){
        //             years[year] = {}
        //             for(let programme in eventsByYear[year]){
        //                 years[year][programme] = { }
        //                 for(let e in eventsByYear[year][programme]){
        //                     const event = eventsByYear[year][programme][e]
        //                     if(!years[year][programme][event.id]) years[year][programme][event.id] = event
                            
        //                 } 
        //             }
        //         }

        //         this.$set(this.daysByYearAndMonth[date.getFullYear()][date.getMonth()][date.getDate()], 'eventsByYear', years);
        //     }

        //     this.weekOptions = weekOptions;
        //     this.numWeeks = weekNo;
        //     this.numDays = day;
        //     this.startDate = start;
        //     this.endDate = end;
        // },

        updateDays() {
            const eventContainer = !this.editMode ? this.eventContainer : this.eventEditor;
            const from = this.scrollPaginate ? this.scrollData.from : this.from;
            const until = this.scrollPaginate ? this.scrollData.until : this.until;
            if (!from || !until) return;
            
            this.daysByYearAndMonth = {};
            this.weekStartDates = [];
            let day = 0;
            let weekNo = 0;
            let weekOptions = {};
            
            const start = dateFromISODate(from);
            const end = dateFromISODate(until);
            let date = new Date(start);
            
            while (date.getDay() !== 1) date.setDate(date.getDate() - 1);
            while (date <= end) {
                weekNo++;
                const weekStart = new Date(date);
                this.weekStartDates.push(weekStart);
                const dayNum = weekStart.getDate();
                const month = weekStart.toLocaleString('default', { month: 'short' });
                const year = weekStart.getFullYear();
                const suffix = (d) => {
                    if (d > 3 && d < 21) return 'th';
                    switch (d % 10) {
                        case 1: return 'st';
                        case 2: return 'nd';
                        case 3: return 'rd';
                        default: return 'th';
                    }
                };
                weekOptions[`${weekNo}`] = `W/C ${dayNum}${suffix(dayNum)} ${month} ${year}`;
                date.setDate(date.getDate() + 7);
            }
    
            // Reset date to real start
            date = new Date(start);
            let week = date.getDay() === 1 ? 1 : 0;
        
            for (; date <= end; date.setDate(date.getDate() + 1)) {
                day++;
                if (date.getDay() === 1) week++;
                if (this.hideWeekends && (date.getDay() === 6 || date.getDay() === 0)) continue;
                
                const year = date.getFullYear();
                const month = date.getMonth();
                const dateNum = date.getDate();
                
                if (!this.daysByYearAndMonth[year]) {
                    this.$set(this.daysByYearAndMonth, year, {});
                }
                if (!this.daysByYearAndMonth[year][month]) {
                    this.$set(this.daysByYearAndMonth[year], month, {});
                }
                
                if (this.dense && eventContainer.isEmpty(year, month, dateNum)) continue;
                
                this.$set(this.daysByYearAndMonth[year][month], dateNum, {
                    day: date.getDay(),
                    week,
                    eventsByGroup: eventContainer.getGroupEvents(year, month, dateNum),
                    eventsByStudent: eventContainer.getStudentEvents(year, month, dateNum)
                });
                
                // Add eventsByYear
                const eventsByYear = eventContainer.getYearEvents(year, month, dateNum);
                let years = {};
                for (let y in eventsByYear) {
                    years[y] = {};
                    for (let programme in eventsByYear[y]) {
                        years[y][programme] = {};
                        for (let e in eventsByYear[y][programme]) {
                            const event = eventsByYear[y][programme][e];
                            if (!years[y][programme][event.id]) {
                                years[y][programme][event.id] = event;
                            }
                        }
                    }
                }
                this.$set(this.daysByYearAndMonth[year][month][dateNum], 'eventsByYear', years);
            }
            
            this.weekOptions = weekOptions;
            this.numWeeks = weekNo;
            this.numDays = day;
            this.startDate = start;
            this.endDate = end;
        },
        setLoading() {
            this.loadingEvents = new Promise((resolve) => {
                this.resolveLoading = resolve
            })
        },
        hasEventsForYearAndProgramme(day, year, programmeId) {
            if (!day.eventsByYear || !day.eventsByYear[year]) {
                return false;
            }
            return Object.keys(day.eventsByYear[year]).some(progId => progId == programmeId);
        },
        filteredEventsByYear(day, year, programmeId) {
            if (!day.eventsByYear || !day.eventsByYear[year] || !day.eventsByYear[year][programmeId]) {
                return [];
            }
            const events = Object.values(day.eventsByYear[year][programmeId]);
            return events.filter(e => this.matchesRoomFilter(e));
        },
        nextWeek() {
            if (this.scrollData.loadingEarlier || this.scrollData.loadingLater) return

            const leftDay = this.getLeftOfScreenDay(7)

            const prevX = this.scrollData.prevX
            this.progScrollX(leftDay.offsetLeft - this.$refs.topLeftCell.offsetWidth + 1, true, 'scroll', false)

            if (prevX === this.scrollData.prevX && this.dense) this.goForward()
        },
        prevWeek() {
            if (this.scrollData.loadingEarlier || this.scrollData.loadingLater) return

            const leftDay = this.getLeftOfScreenDay(-7, true)

            this.progScrollX(leftDay.offsetLeft - this.$refs.topLeftCell.offsetWidth, false, 'scroll', false)
        },
        matchesRoomFilter(event) {
            // If no rooms are selected, show everything:
            if (!this.rooms || !this.rooms.length) return true
            // Otherwise, only show events whose room is in the rooms array:
            return this.rooms.some(r => r.id === event.ttroom_id)
        },
        toWeek(week) {
            if (!this.timetable || !week) return;

            const date = dateFromISODate(this.timetable.datefrom);
            date.setDate(date.getDate() + (week - 1) * 7);

            while (date.getDay() !== 1) {
                date.setDate(date.getDate() - 1);
            }

            const from = dateToISODate(date);

            const currentFrom = this.$route.query.from;
            if (currentFrom === from) {
                this.$emit('refreshWeek', from); 
                return;
            }

            this.$router.push({
                path: `/timetables/edit/${this.timetable.id}`,
                query: { from }
            });
        },

        /**
         * If there are previous dates to fetch and we aren't busy, fetch them
         */
        goBack() {
            if (!this.scrollData.loadingEarlier && !(this.from <= this.minDate)) {
                this.setLoading()
                this.scrollData.loadingEarlier = true
                this.$emit('back', this.scrollData.from)
            }
        },
        /**
         * If there are previous dates to fetch and we aren't busy, fetch them
         */
        goForward() {
            if (!this.scrollData.loadingLater && !(this.until >= this.maxDate)) {
                this.setLoading()
                this.scrollData.loadingLater = true
                this.$emit('forward', this.scrollData.until)
            }
        },
        /**
         * Watches horizontal scroll position to paginate through events.
         * 
         * @param {Event} event 
         */
        handleScroll(event) {
            if (!this.ready) return // Guard added
            this.handleHoriScroll(event)
            if (this.studentView) this.handleVertScroll(event)

            const x = event.target.scrollLeft
            const y = event.target.scrollTop

            this.$nextTick(() => {
                this.scrollData.prevX = x
                this.scrollData.prevY = y
            })
        },
        handleHoriScroll(event) {
            if (!this.ready) return
            if (this.scrollData.progScrollX) {
                this.scrollData.progScrollX = false
                return
            }
            const forward = this.scrollData.prevX <= event.target.scrollLeft

            if (forward) {
                const fromEnd = (event.target.scrollWidth - (event.target.scrollLeft + event.target.offsetWidth)) / event.target.scrollWidth
                if (fromEnd < 0.2) this.goForward()
            } else {
                const fromStart = event.target.scrollLeft / event.target.scrollWidth
                if (fromStart < 0.2) this.goBack()
            }
        },
        handleVertScroll(event) {
            if (this.scrollData.progScrollY) {
                this.scrollData.progScrollY = false
                return
            }

            const down = this.scrollData.prevY < event.target.scrollTop

            if (down) {
                const fromEnd = (event.target.scrollHeight - (event.target.scrollTop + event.target.offsetHeight)) / event.target.scrollHeight
                if (fromEnd < 0.1 && this.studentWindow.down()) {
                    let shiftLen = 0
                    for (const row of this.$refs.students.slice(0, this.studentWindow.interval)) shiftLen += row.offsetHeight

                    const leftDay = this.getLeftOfScreenDay()
                    const prevOffset = leftDay.offsetLeft
                    this.$nextTick(() => {
                        if (!this.scrollData.loading()) this.progScrollX(leftDay.offsetLeft - this.$refs.topLeftCell.offsetWidth, prevOffset < leftDay.offsetLeft)
                        this.progScrollY(event.target.scrollTop-shiftLen, down)
                    })
                }
            } else {
                const fromStart = event.target.scrollTop / event.target.scrollHeight

                if (fromStart < 0.1 && this.studentWindow.up()) {
                    this.progScrollY(event.target.scrollTop, down)

                    const leftDay = this.getLeftOfScreenDay()
                    const prevOffset = leftDay.offsetLeft
                    this.$nextTick(() => {
                        if (!this.scrollData.loading()) this.progScrollX(leftDay.offsetLeft - this.$refs.topLeftCell.offsetWidth, prevOffset < leftDay.offsetLeft)
                    })
                }
            }
        },
        /**
         * Programmatically scroll to x pix.
         * 
         * @param {integer} x Sets horizontal scroll to x
         * @param {boolean} forward Direction of scroll
         * @param {string} funcName specifies which scroll function to call, i.e. 'scroll', 'scrollTo'
         * @param {boolean} block If false, scroll does not block user-triggered scroll events, i.e. data fetching.
         */
        progScrollX(x, forward, funcName='scroll', block=true) {
            if (block) {
                if (this.scrollData.progScrollX) return
                this.scrollData.progScrollX = true
            }

            this.$refs.timetable[funcName]({ left: x })
            this.scrollData.prevX = forward ? 0 : this.$refs.timetable.scrollWidth
        },
        /**
         * Programmatically scroll to y pix.
         * 
         * @param {integer} y Sets vertical scroll to y
         * @param {boolean} down
         * @param {string} funcName Can be used to specify another scroll method, such as 'scrollBy'
         */
        progScrollY(y, down, funcName='scroll') {
            this.scrollData.progScrollY = true

            this.$refs.timetable[funcName]({ top: y })
            this.scrollData.prevY = down ? 0 : this.$refs.timetable.scrollHeight
        },
        getLeftOfScreenDay(offset=0, leftBias=false) {
            if (!this.$refs.days || this.$refs.days.length < 5) return this.$refs.days?.[0];
            //if (this.$refs.days.length < 5) return this.$refs.days[0]

            const border = this.$refs.timetable.scrollLeft + this.$refs.topLeftCell.offsetWidth

            let leftDay
            let rightDay

            this.$refs.days.sort((a, b) => {
                return a.offsetLeft < b.offsetLeft ? -1 : 1
            })

            for (let i = 1; i < this.$refs.days.length; ++i) {
                if (this.$refs.days[i - 1].offsetLeft <= border && this.$refs.days[i].offsetLeft > border) {
                    if (offset < 0) {
                        i = Math.max(1, i + offset)
                    }
                    else if (offset > 0) {
                        i = Math.min(this.$refs.days.length - 1, i + offset)
                    }

                    leftDay = this.$refs.days[i - 1]
                    rightDay = this.$refs.days[i]

                    break
                }
            }
            
            return (rightDay.offsetLeft - border > border - leftDay.offsetLeft) || leftBias ? leftDay : rightDay
        },
        /**
		 * Determines if it is possible for the user to scroll.
		 * We use this to load more data if the user cannot trigger a scroll-event.
		 */
		canScroll() {
            const isOverflowing = this.$refs.timetable.clientWidth < this.$refs.timetable.scrollWidth
			return isOverflowing;
		},
        scrollToCurrentWeek() {
             if (!this.$refs.days || !this.$refs.days.length) return;

            const today = new Date();
            const monday = new Date(today);
            const day = monday.getDay();
            const diff = day === 0 ? -6 : 1 - day;
            monday.setDate(monday.getDate() + diff);
            monday.setHours(0, 0, 0, 0);

            for (let i = 0; i < this.$refs.days.length; i++) {
                const parts = this.$refs.days[i].id.split('-');
                if (parts.length !== 3) continue; 

                const [year, month, date] = parts.map(Number);
                const refDate = new Date(year, month, date);
                refDate.setHours(0, 0, 0, 0);

                if (refDate.getTime() === monday.getTime()) {
                    this.progScrollX(this.$refs.days[i].offsetLeft, true);
                    this.$refs.timetable.scrollTop = 0;
                    break;
                }
            }

            this.$nextTick(() => {
                this.scrollData.progScrollX = false;
            });
        },

        modulo(x, n) {
            return ((x % n) + n) % n
        },
        getEventGroups() {
            const groupsById = {}

            this.events.forEach(event => {
                if (!event.groups) return
                event.groups.forEach(group => groupsById[group.id] = group)
            })

            return Object.values(groupsById)
        },
        /**
         * Determine if a student is marked absent at any point in the day passed.
         * 
         * If event is not null, also checks that the student is absent at the time of the event.
         * 
         * @param {integer|string} year E.g. 1999
         * @param {integer|string} month 0-11
         * @param {integer|string} date 1-31
         * @param {integer} studentId 
         * @param {object} event 
         */
        isAbsent(year, month, date, studentId, event=null) {
            const key = EventContainer.getKey(year, month, date)

            if (!this.absencesByStudent[key]) return false
            if (!event) return this.absencesByStudent[key] && this.absencesByStudent[key][studentId]

            const bounds = Math.min(1000*60*15, Number(dateFromTimestamp(event.to) - dateFromTimestamp(event.from)) /  2)
            
            const eventFrom = dateFromTimestamp(event.from)
            const eventTo = new Date(eventFrom.getTime() + bounds);

            const absence = this.absencesByStudent[key][studentId]
            
            return !absence ? false : absence.datefrom <= eventFrom && eventTo <= absence.dateto
        },
        clearEvents() {
            this.eventContainer = new EventContainer
            this.scrollData.ranges = []
        },
        toggleEditMode(val) {
            this.editMode = val

            if (this.editMode && !this.eventEditor) {
                this.eventEditor = new EventEditor(this.eventContainer)
                //this.eventEditor.loadEvents(this.events)
                this.updateDays()
            }
            else {
                this.addMode = false
                this.addEvents = []
                if (this.eventEditor) this.eventEditor.resetSelected()
                this.updateDays()
            }
        },
        toggleAddMode(val) {
            this.addMode = val

            if (!this.addMode) {
                this.addEvents = []
                this.eventEditor.resetSelected()
            }
        },
        /**
         * @param {integer|string} year E.g. 1999
         * @param {integer|string} month 0-11
         * @param {integer|string} date 1-31
         * @param {string|number} student
         */
        toggleDay(year, month, date, student) {
            if (this.eventEditor.dayIsSelected(year, month, date, student.id)) {
                this.addEvents = this.eventEditor.unselectDay(year, month, date, student.id)
            } else {
                const from = new Date(year, month, date, 0, 0, 0).getTime()/1000
                const to = new Date(year, month, date, 23, 59, 59).getTime()/1000

                const availableEvents = this.eventContainer.allEvents().filter(e => e.from >= from && e.to <= to)
                this.addEvents = this.eventEditor.selectDay(year, month, date, student.id, availableEvents)
            }

            this.updateDays()
        },
        /**
         * Takes the session-type, room, start/end times of the given event and adds matching events to all student-date cells selected.
         * 
         * @param {object} event The event-type to add to the selected days.
         */
        addToSelected(event) {
            this.eventEditor.addToSelected(event)
            this.addEvents = []
            this.updateDays()
        },
        /**
         * @param {integer|string} year E.g. 1999
         * @param {integer|string} month 0-11
         * @param {integer|string} date 1-31
         * @param {integer|string} studentId
         * @param {integer|string} eventId
         */
        toggleEvent(year, month, date, studentId, eventId) {
            this.eventEditor.toggleEvent(year, month, date, studentId, eventId)
            this.updateDays()
        },
        removeSelected() {
            this.eventEditor.removeSelected()
            this.updateDays()
        },
        undo() {
            if (this.eventEditor) {
                this.eventEditor.undo()
                this.updateDays()
            }
        },
        showSaveDialog() {
            if (!this.timetable) return

            if (this.timetable.status != 2) {
                this.saveEdit()
                return
            }

            this.$store.commit('openDialog', {
                title: this.$t('saving-events'),
                content: this.$t('notify-students-question'),
                cancelBtn: true,
                confirmCallBack: () => {
                    this.notifyStudents = true
                    this.saveEdit().then(() => {
                        this.notifyStudents = false
                    })
                },
                confirmBtnText: this.$t('notify'),
                cancelBtnText: this.$t('no'),
                cancelCallBack: this.saveEdit,
            })
        },
        saveEdit() {
            const data = this.eventEditor.dumpEdit()
            if (data.add.length + data.remove.length === 0) return Promise.resolve()
            
            if (this.submitting) return Promise.resolve()
            this.submitting = true
            this.$store.commit('showBlockingSpinner')
            
            if (this.notifyStudents) data.notify = true

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/students/events/save`, {
                method: 'POST',
                data,
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.saveState()
                this.$emit('saveState')
                this.$nextTick(() => {
                    this.$router.go(0)
                })
            }).catch(err => {
                this.$log.error.call(this, err)
                console.error(err)

                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-tt-edit'),
                })
            }).finally(() => {
                this.submitting = false
                this.eventEditor = new EventEditor
                this.$store.commit('hideBlockingSpinner')
            })
        }
    },
    computed: {
        showJumpToTodayButton() {
            const todayISO = new Date().toISOString().split('T')[0];
            return todayISO >= this.minDate && todayISO <= this.maxDate;
        },

        firstWeekLen() {
            if (!this.startDate) return 0

            const n = this.hideWeekends ? 5 : 7
            return this.modulo(n - this.startDate.getDay(), n) + 1
        },
        lastWeekLen() {
            if (!this.endDate) return 0
            return this.endDate.getDay() === 0 ? 7 : this.endDate.getDay()
        },
        zoomClass() {
            return this.zoom == 2 
            ? 'table-timetable-sm' 
            : this.zoom == 1 
            ? 'table-timetable-xs' 
            : ''
		},
        filteredStudents() {
            if (this.studentsAreTeachers) return this.students
            
            const students = this.students.filter(s => {
                return (!this.years.length || this.years.findIndex(y => y == s.year) > -1)
                &&  (!this.groups.length || this.groups.findIndex(g => !!g.grouptype_id && g.id == s.group_id || (g.year == s.year && g.group_id == s.group_id)) > -1)
                && (!this.firm || s.firm_id == this.firm)
            })

            // The below line is a bit cheeky, but it works

            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.studentWindow.setTotal(students.length)
            
            return students.slice(
                this.scrollPaginate ? this.studentWindow.from : 0, 
                this.scrollPaginate ? this.studentWindow.to : this.studentWindow.total
            )
        },
       
    },
    watch: {
        events: {
            deep: true, 
            immediate: true,
            handler(newEvents) {
                this.innerEvents = newEvents
            },
        },
        innerEvents: {
            deep: true,
            handler(newEvents) {
                this.scrollData.loadingEarlier = false
                this.scrollData.loadingLater = false
                
                if (!this.scrollPaginate) {
                    this.eventContainer = new EventContainer(newEvents, this.bgEvents)
                    this.updateDays()

                    return
                }
                
                newEvents.forEach(e => this.eventContainer.insertEvent(e))
                
                if (!this.scrollData.ranges.length) {
                    this.scrollData.ranges.push({from: this.from, until: this.until})
                    this.scrollData.from = this.from
                    this.scrollData.until = this.until
                }

                if (this.until > this.scrollData.ranges.slice(-1)[0].until) {
                    this.scrollData.ranges.push({
                        from: this.from > this.maxDate ? this.maxDate : this.from,
                        until: this.until < this.minDate ? this.minDate : this.until,
                    })

                    this.scrollData.until = this.until

                    if (this.scrollData.ranges.length >= 4 && newEvents.length > 10) {
                        const clear = this.scrollData.ranges.shift()
                        this.eventContainer.clearRange(clear.from, clear.until)

                        this.scrollData.from = this.scrollData.ranges[0].from
                        const newFrom = dateFromISODate(this.scrollData.ranges[0].from)
                        
                        let fromEl = document.getElementById(`${newFrom.getFullYear()}-${newFrom.getMonth()}-${newFrom.getDate()}`)

                        if (this.dense) {
                            const maxFrom = dateFromISODate(this.maxDate)

                            while (!fromEl && newFrom < maxFrom) {
                                newFrom.setDate(newFrom.getDate() + 1)
                                fromEl = document.getElementById(`${newFrom.getFullYear()}-${newFrom.getMonth()}-${newFrom.getDate()}`)
                            }
                        }

                        if (fromEl) {
                            const shiftLen = fromEl.offsetLeft - this.$refs.topLeftCell.offsetWidth
                            this.progScrollX(this.scrollData.prevX - shiftLen, true)
                        }
                    }
                    
                    this.updateDays()
                }
                else if (this.from < this.scrollData.ranges[0].from) {
                    this.scrollData.ranges.unshift({
                        from: this.from > this.maxDate ? this.maxDate : this.from,
                        until: this.until < this.minDate ? this.minDate : this.until,
                    })

                    this.scrollData.from = this.from

                    if (this.scrollData.ranges.length >= 4 && newEvents.length > 10) {
                        const clear = this.scrollData.ranges.pop()
                        this.eventContainer.clearRange(clear.from, clear.until)
                        this.scrollData.until = this.scrollData.ranges.slice(-1)[0].until
                    }

                    const oldFrom = dateFromISODate(this.scrollData.ranges[1].from)
                    let fromEl = document.getElementById(`${oldFrom.getFullYear()}-${oldFrom.getMonth()}-${oldFrom.getDate()}`)

                    if (this.dense) {
                        const minFrom = dateFromISODate(this.minDate)

                        while (!fromEl && oldFrom > minFrom) {
                            oldFrom.setDate(oldFrom.getDate() - 1)
                            fromEl = document.getElementById(`${oldFrom.getFullYear()}-${oldFrom.getMonth()}-${oldFrom.getDate()}`)
                        }
                    }

                    this.updateDays()
                    
                    if (fromEl) {
                        const prevOffset = fromEl.offsetLeft
                        this.$nextTick(() => {
                            const shiftLen = fromEl.offsetLeft - prevOffset
                            this.progScrollX(this.scrollData.prevX + shiftLen, false)
                        })
                    }
                }
            },
        },
        bgEvents: {
            deep: true,
            handler(newEvents) {
                if (!newEvents.length) {
                    this.updateDays()
                    return
                }
                this.eventContainer = new EventContainer(this.events, newEvents)
                this.updateDays()
            },
        },
        groups: {
            deep: true,
            immediate: true,
            handler(newGroups) {
                this.groupsByYear = {}
                newGroups.forEach(group => {
                    if (!this.groupsByYear[group.year]) this.groupsByYear[group.year] = []
                    this.groupsByYear[group.year].push(group)
                })
            },
        },
        students: {
            deep: true,
            immediate: true,
            handler(newStudents) {
                this.studentsByYear = {}
                newStudents.forEach(student => {
                    if (!this.studentsByYear[student.year]) this.studentsByYear[student.year] = []
                    this.studentsByYear[student.year].push(student)
                })
            },
        },
        rooms: {
            deep: true,
            immediate: true,
            handler() {
                this.isFiltering = true
                this.$nextTick(() => {
                    this.isFiltering = false
                })
                
            },
        },
        absences: {
            deep: true,
            immediate: true,
            handler(newAbsences) {
                this.absencesByStudent = {}
                newAbsences.forEach(absence => {
                    absence.datefrom = dateFromTimestamp(absence.dateabsent)
                    if (absence.dateabsentend) absence.dateto = dateFromTimestamp(absence.dateabsentend)
                    else {
                        absence.dateto = new Date(absence.datefrom)
                        absence.dateto.setHours(23, 59, 59, 999)
                    }

                    let key
                    for (const day = new Date(absence.datefrom); day <= absence.dateto; day.setDate(day.getDate() + 1)) {
                        key = dateToISODate(day) 
                        if (!this.absencesByStudent[key]) this.absencesByStudent[key] = {}
                        this.absencesByStudent[key][absence.student_id] = absence
                    }
                })
            }
        },
        dense() {
            this.updateDays()
        },
        hideWeekends() {
            this.updateDays()
        }
    },
}
</script>